import { format, parseISO } from 'date-fns';
import { FunctionComponent, memo, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { ConfigContext } from '../../../context';
import { UserPreview } from '../../../services';
import { Modal } from '../Modal';
import { CHANGE_LOG_DATE_FORMAT, useFormatChange } from './formatChange';

import classes from './ChangeLogModal.module.scss';

export interface ChangeLogChange {
  path: string;
  to: unknown;
  from: unknown;
}

export interface ChangeLog {
  id: number;
  createdAt: string;
  changes: ChangeLogChange[];
  user: UserPreview | null;
}

interface ChangeLogModalProps {
  isLoading: boolean;
  isOpen: boolean;
  items: ChangeLog[];
  onClose: () => void;
}

export const ChangeLogModal: FunctionComponent<ChangeLogModalProps> = memo(
  ({ isLoading, isOpen, items, onClose }) => {
    const { t: ctxT } = useTranslation('', { keyPrefix: 'changeLog' });

    const formatChange = useFormatChange();

    const { changeLog } = useContext(ConfigContext).config.elements;

    const { showNameColumn = false, showEmailColumn = false } = changeLog ?? {};

    const content = useMemo(() => {
      if (isLoading) {
        return (
          <div className={classes['change-log__loader']}>
            <Skeleton height={'3rem'} />
            <Skeleton height={'3rem'} />
            <Skeleton height={'3rem'} />
          </div>
        );
      }

      if (items.length === 0) {
        return <div className={classes['change-log__empty']}>{ctxT('empty')}</div>;
      }

      return (
        <table className={classes['change-log__table']}>
          <thead>
            <tr>
              <th className={classes['change-log__date']}>{ctxT('date')}</th>
              {showNameColumn && <th className={classes['change-log__name']}>{ctxT('name')}</th>}
              {showEmailColumn && <th className={classes['change-log__email']}>{ctxT('email')}</th>}
              <th>{ctxT('changes')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(({ id, createdAt, user, changes }) => (
              <tr key={id}>
                <td className={classes['change-log__date']}>
                  {format(parseISO(createdAt), CHANGE_LOG_DATE_FORMAT)}
                </td>
                {showNameColumn && (
                  <td className={classes['change-log__name']}>{user?.screenName}</td>
                )}
                {showEmailColumn && <td className={classes['change-log__email']}>{user?.email}</td>}
                <td>
                  <ul className={classes['change-log__changes']}>
                    {changes.map((change) => (
                      <li
                        key={change.path}
                        dangerouslySetInnerHTML={{ __html: formatChange(change) }}
                      />
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }, [ctxT, formatChange, isLoading, items, showEmailColumn, showNameColumn]);

    const title = ctxT('title');

    const body = (
      <div className={classes['change-log']}>
        <div className={classes['change-log__title']}>{title}</div>
        {content}
      </div>
    );

    return (
      <Modal
        isOpen={isOpen}
        body={body}
        onClose={onClose}
        contentStyle={{ width: '95vw' }}
        ariaLabel={title}
        alignTop
      />
    );
  }
);
